import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Stack, useTheme } from "@mui/material";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import Button from "components/basic/button";
import Typography from "components/basic/typography";
import LoadingSpinner from "components/basic/loadingSpinner";

import { DateField } from "../../../../../../../setAvailability/pickers";
import TimeSlotPicker from "../../../../../../../../meetings/components/TimeSlotPicker";
import {
  Slot,
  horizonMaxDate,
  earliestBookableDate,
} from "../../../../../../../../meetings/helpers/availabilitySlots";
import {
  useBookableVendors,
  useQuoteVendorAvailability,
} from "../../../../../BookAppointmentModal/bookAppointmentApi";
import { Booking } from "../../../../../../types";
import { ProposedTime } from "../../../../../../bookingsApi";

// Slot length to offer: the meeting keeps its own length (the picker rounds it
// up to a whole 30-minute slot).
const durationOf = (booking: Booking): number =>
  booking.allDay
    ? 30
    : moment(booking.to, "HH:mm").diff(
        moment(booking.from, "HH:mm"),
        "minutes",
      );

/**
 * "Propose a new time" for an invited customer. It reads the host's
 * availability through the same path as the Book Appointment modal — one of
 * the customer's quotes with that vendor — so the open times shown here are
 * exactly the ones shown when booking a new appointment.
 */
const RescheduleForm: React.FC<{
  booking: Booking;
  responding?: boolean;
  onCancel: () => void;
  onSubmit: (proposed: ProposedTime) => void;
}> = ({ booking, responding, onCancel, onSubmit }) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");

  // The vendor's availability is exposed per quote, so use this booking's
  // quote — or any other quote the customer has with the same vendor.
  const vendors = useBookableVendors(true);
  const quoteId = useMemo(() => {
    const quotes =
      vendors.find((v) => v.vendorId === booking.vendorId)?.quotes ?? [];
    return (
      quotes.find((q) => q.quoteId === booking.quoteId)?.quoteId ??
      quotes[0]?.quoteId
    );
  }, [vendors, booking.vendorId, booking.quoteId]);

  const { loading, availabilityConfigured, availability, busyBookings } =
    useQuoteVendorAvailability(quoteId);

  const today = moment().format("YYYY-MM-DD");
  const [date, setDate] = useState(
    booking.date >= today ? booking.date : today,
  );
  const [slot, setSlot] = useState<Slot | null>(null);

  // This meeting shouldn't block the times around itself.
  const otherBookings = useMemo(
    () =>
      busyBookings.filter(
        (b) =>
          !(
            b.date === booking.date &&
            b.from === booking.from &&
            b.to === booking.to
          ),
      ),
    [busyBookings, booking.date, booking.from, booking.to],
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Stack spacing={1} sx={{ mt: 1.5 }}>
        <Typography variant="caption" style={{ fontWeight: 600 }}>
          {t("details.propose")}
        </Typography>

        {!availabilityConfigured || !availability ? (
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary }}
          >
            {t("details.no_availability")}
          </Typography>
        ) : (
          <>
            <DateField
              value={date}
              onChange={(d) => {
                setDate(d);
                setSlot(null);
              }}
              minDate={earliestBookableDate(availability)}
              maxDate={horizonMaxDate(availability.bookingHorizon)}
            />

            <Typography
              variant="caption"
              style={{ color: theme.palette.text.secondary }}
            >
              {t("details.pick_time")}
            </Typography>

            <TimeSlotPicker
              date={date}
              durationMinutes={durationOf(booking)}
              availability={availability}
              bookings={otherBookings}
              value={slot?.from}
              onSelect={setSlot}
            />
          </>
        )}

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            disabled={responding}
            onClick={onCancel}
          >
            {t("details.cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            fullWidth
            disabled={responding || !slot}
            loading={responding}
            onClick={() =>
              slot && onSubmit({ date, from: slot.from, to: slot.to })
            }
          >
            {t("details.send_proposal")}
          </Button>
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
};

export default RescheduleForm;
